import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Loader2, LogIn, ScanLine } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ReferenceLine,
} from 'recharts';
import { api, type AperturasResumen, type AperturasLlegadas } from '@/lib/api';
import {
  useChartTheme, madridDay, dayLabel, Panel, Stat,
  HeatmapSemanal, TRIGGER_COLORS, TRIGGER_LABELS,
} from '@/components/aperturas/shared';
import type { AperturasContext } from './AperturasLayout';

const TRIGGERS = ['keypad', 'remoto', 'boton', 'manual', 'sistema'];

export default function AperturasPage() {
  const chart = useChartTheme();
  const { days } = useOutletContext<AperturasContext>();
  const [device, setDevice] = useState<number | undefined>(undefined);
  const [data, setData] = useState<AperturasResumen | null>(null);
  const [llegadas, setLlegadas] = useState<AperturasLlegadas | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const from = madridDay(days - 1);
    const to = madridDay();

    Promise.all([api.getAperturasResumen(from, to, device), api.getAperturasLlegadas(from, to)])
      .then(([r, l]) => { if (!cancelled) { setData(r); setLlegadas(l); } })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Error cargando datos'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [days, device]);

  const totalAperturas = data?.totalsByCategory.apertura ?? 0;
  const totalCierres = data?.totalsByCategory.cierre ?? 0;
  const identificadas = (data?.totalsByTrigger.keypad ?? 0) + (data?.totalsByTrigger.remoto ?? 0);
  const totalEventos = Object.values(data?.totalsByTrigger ?? {}).reduce((s, n) => s + n, 0);

  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-500 -mt-1">Cuándo se accionan las cerraduras de los apartamentos y por qué medio. Los porteros de portal tienen su propia pestaña.</p>

      <div className="flex justify-end">
        <select
          value={device ?? ''}
          onChange={(e) => setDevice(e.target.value ? Number(e.target.value) : undefined)}
          className="text-sm rounded-lg border border-slate-200 dark:border-white/10 bg-transparent px-3 py-1.5 text-slate-600 dark:text-slate-300"
        >
          <option value="">Todos los apartamentos</option>
          {(data?.devices ?? []).map((d) => (
            <option key={d.smartlockId} value={d.smartlockId}>{d.deviceLabel}</option>
          ))}
        </select>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 className="animate-spin mr-2" size={20} /> Cargando…
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 dark:bg-red-500/10 dark:border-red-500/30 p-4 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {data && !loading && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="Aperturas" value={totalAperturas.toLocaleString('es-ES')} hint={`${(totalAperturas / data.spanDays).toFixed(1)}/día`} />
            <Stat label="Cierres" value={totalCierres.toLocaleString('es-ES')} />
            <Stat
              label="Con usuario identificado"
              value={totalEventos ? `${((100 * identificadas) / totalEventos).toFixed(0)}%` : '—'}
              hint="teclado o app"
            />
            <Stat label="Códigos rechazados" value={(data.totalsByCategory.rechazo ?? 0).toLocaleString('es-ES')} />
          </div>

          <Panel
            title="Reloj semanal de aperturas"
            hint="Color = aperturas de un lunes medio a esa hora, normalizado por el número de lunes del rango. Pasa el ratón por una celda para ver el total absoluto."
          >
            <HeatmapSemanal cells={data.heatmap} />
          </Panel>

          <Panel
            title="Medio de apertura por hora"
            hint="Un evento manual o de botón no lleva usuario: Nuki no informa de quién fue."
          >
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={data.hours} margin={{ top: 4, right: 16, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                <XAxis dataKey="hour" tickFormatter={(h) => `${h}h`} tick={{ fontSize: 11, fill: chart.axis }} />
                <YAxis tick={{ fontSize: 12, fill: chart.axis }} allowDecimals={false} />
                <Tooltip
                  cursor={{ fill: chart.cursor }}
                  labelFormatter={(h) => `${h}:00 – ${h}:59`}
                  formatter={(v: any, k: any) => [v, TRIGGER_LABELS[k] ?? k]}
                />
                <Legend formatter={(k) => TRIGGER_LABELS[k] ?? k} wrapperStyle={{ fontSize: 12 }} />
                {TRIGGERS.map((t) => (
                  <Area key={t} type="monotone" dataKey={t} stackId="1" stroke={TRIGGER_COLORS[t]} fill={TRIGGER_COLORS[t]} fillOpacity={0.75} />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </Panel>

          <Panel title="Evolución diaria" hint="Aperturas y cierres registrados por día.">
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={data.series} margin={{ top: 4, right: 16, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                <XAxis dataKey="day" tickFormatter={dayLabel} tick={{ fontSize: 11, fill: chart.axis }} minTickGap={28} />
                <YAxis tick={{ fontSize: 12, fill: chart.axis }} allowDecimals={false} />
                <Tooltip cursor={{ fill: chart.cursor }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="apertura" name="Aperturas" stroke={chart.palette[0]} fill={chart.palette[0]} fillOpacity={0.25} />
                <Area type="monotone" dataKey="cierre" name="Cierres" stroke={chart.palette[2]} fill={chart.palette[2]} fillOpacity={0.25} />
              </AreaChart>
            </ResponsiveContainer>
          </Panel>

          {/* Primeras llegadas */}
          {llegadas && (
            <Panel
              title="Primera llegada del huésped"
              hint={
                <>
                  {llegadas.total} llegadas registradas. <strong className="font-medium text-slate-500">Ojo</strong>: solo se
                  registra la llegada si el código se usa el mismo día del check-in, así que una entrada a las 00:40 del día
                  siguiente no aparece nunca y el histograma está truncado en la medianoche.
                </>
              }
              action={
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  <LogIn size={14} className="text-slate-400" />
                  <span>p25 <strong className="text-sereno-dark">{llegadas.p25 ?? '—'}</strong></span>
                  <span>mediana <strong className="text-sereno-dark">{llegadas.p50 ?? '—'}</strong></span>
                  <span>p90 <strong className="text-sereno-dark">{llegadas.p90 ?? '—'}</strong></span>
                </div>
              }
            >
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={llegadas.bins} margin={{ top: 4, right: 16, left: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                  <XAxis
                    dataKey="etiqueta"
                    tick={{ fontSize: 10, fill: chart.axis }}
                    interval={3}
                  />
                  <YAxis tick={{ fontSize: 12, fill: chart.axis }} allowDecimals={false} />
                  <Tooltip cursor={{ fill: chart.cursor }} formatter={(v: any) => [`${v} llegadas`, '']} />
                  {llegadas.p50 && <ReferenceLine x={`${llegadas.p50.slice(0, 3)}${Number(llegadas.p50.slice(3)) >= 30 ? '30' : '00'}`} stroke={chart.palette[1]} strokeDasharray="4 4" />}
                  <Bar dataKey="llegadas" fill={chart.bar} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Panel>
          )}

          {/* Sensor de puerta */}
          <Panel
            title="Puertas con sensor"
            hint="Los únicos dispositivos donde se sabe que la puerta se movió de verdad, y no solo que giró el motor. En el resto de la flota este dato no existe."
            action={
              <div className="flex items-center gap-3 text-xs text-slate-500">
                <ScanLine size={14} className="text-slate-400" />
                <span>mediana abierta <strong className="text-sereno-dark">{data.sensor.p50 ?? '—'}s</strong></span>
                <span>p90 <strong className="text-sereno-dark">{data.sensor.p90 ?? '—'}s</strong></span>
              </div>
            }
          >
            {data.sensor.devices.length === 0 ? (
              <p className="text-sm text-slate-400">Sin lecturas de sensor en el rango.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-slate-500">
                  <tr>
                    <th className="text-left font-medium pb-2">Apartamento</th>
                    <th className="text-right font-medium pb-2">Veces abierta</th>
                    <th className="text-right font-medium pb-2">Mediana abierta</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sensor.devices.map((d) => (
                    <tr key={d.smartlockId} className="border-t border-slate-100 dark:border-white/5">
                      <td className="py-2 text-sereno-dark">{d.deviceLabel}</td>
                      <td className="py-2 text-right tabular-nums">{d.aperturas.toLocaleString('es-ES')}</td>
                      <td className="py-2 text-right tabular-nums text-slate-500">
                        {d.medianaSegundos === null ? '—' : `${d.medianaSegundos}s`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}
